import NOTIFICATIONS from 'components/ToastNotification/constants';
import { NotificationContext } from 'containers/Notification';
import React, { useContext, useState } from 'react';
import analytics from 'utils/analytics';
import { orderMembershipKit } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { APP_ROOT } from 'utils/constants/urls';
import { logError } from 'utils/helpers';
import { UserSettingsContext } from '../useAccountSettings/context';
import { TestPlanContext } from '../useTestPlan/context';
import {
  TestPlanItem,
  TestPlanState,
  TestPlanStatusIds,
} from '../useTestPlan/types';
import { UserContext } from '../useUserContext/context';

const APP_ROOT_URL: string = APP_ROOT;

export const useCreditRedemptionModal = () => {
  const [_variantId, _setVariantId] = useState<number | null>(null);
  const [_testPlanItem, _setTestPlanItem] = useState<TestPlanItem | null>(null);

  const { updateTestPlanItem, setTestPlanActiveStatus } =
    useContext(TestPlanContext);
  const { getTestHistory } = useContext(UserSettingsContext);
  const { setNotificationData } = useContext(NotificationContext);

  const {
    runEffect,
    userData: { userId, isMember },
  } = useContext(UserContext);
  const [_openCreditRedemptionModal, _setOpenCreditRedemptionModal] =
    useState(false);

  const openCreditRedemptionModal = (
    variantId: number,
    testPlanItem?: TestPlanItem,
  ) => {
    _setOpenCreditRedemptionModal(true);
    _setVariantId(variantId);
    testPlanItem && _setTestPlanItem(testPlanItem);
  };

  const closeCreditRedemptionModal = async () => {
    _setOpenCreditRedemptionModal(false);
    _setTestPlanItem(null);
    await analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.ORDER_MODAL_CLOSE.label,
        description: ANALYTICS.LABELS.ORDER_MODAL_CLOSE.description,
        isMember,
      },
    });
  };

  const setVariantId = (variantId: number) => {
    _setVariantId(variantId);
  };

  const redirect = (orderId: string | number) =>
    (window.location.href = `${APP_ROOT_URL}/confirmation?orderId=${orderId}&?membershipCreditRedemption=true`);

  const processCreditRedemptionModalOrder = async () => {
    try {
      if (_variantId && userId) {
        const { id: orderId } = await orderMembershipKit(_variantId, userId);
        if (_testPlanItem) {
          // Remove item from test plan
          await updateTestPlanItem({
            ..._testPlanItem,
            attributes: {
              ..._testPlanItem.attributes,
              state: TestPlanState.in_progress,
            },
          });
          //Switch to Test Plan completed tab
          await setTestPlanActiveStatus(TestPlanStatusIds.completed);
        }
        setNotificationData(
          NOTIFICATIONS.MBRS_CREDIT_REDEMPTION_SUCCESS(
            'useCreditRedemptionModal',
          ),
        );
        await runEffect();
        await getTestHistory();
        await closeCreditRedemptionModal();
        redirect(orderId);
      }

      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.ORDER_MODAL_CONFIRM_ORDER.label,
          description: ANALYTICS.LABELS.ORDER_MODAL_CONFIRM_ORDER.description,
          variantId: _variantId || '',
          isMember,
        },
      });
    } catch (err: any) {
      console.error(err);
      logError(err.message, {
        component: 'useCreditRedemptionModal',
        method: 'processCreditRedemptionModalOrder',
      });
      setNotificationData(
        NOTIFICATIONS.MBRS_CREDIT_REDEMPTION_FAILURE(
          'useCreditRedemptionModal',
        ),
      );
    }
  };

  return {
    _openCreditRedemptionModal,
    openCreditRedemptionModal,
    closeCreditRedemptionModal,
    setVariantId,
    processCreditRedemptionModalOrder,
  };
};

export default useCreditRedemptionModal;
